import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  Alert,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Users,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  MousePointerClick,
  Target,
  Percent,
} from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { GoogleAnalyticsIcon } from "../../../components/icons/GoogleAnalyticsIcon";
import { Card, CardContent } from "../../../components/Card";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { TrendDelta } from "../../../components/TrendDelta";
import { MetricCard } from "../../../components/MetricCard";
import { AnalyticsTable } from "../../../components/AnalyticsTable";
import { Progress } from "../../../components/Progress";
import { DeviceShare } from "../../../components/DeviceShare";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { formatNumber, formatPercent } from "../../../utils/numberFormat";
import { ConnectGoogleAnalyticsModal } from "./ConnectGoogleAnalyticsModal";
import { TRAFFIC_CHART_PLOT_HEIGHT, TrafficChart } from "./TrafficChart";
import {
  MetricsDateRangeBar,
  defaultDateSelection,
  toIsoDate,
} from "../../../components/MetricsDateRangeBar";
import {
  getAnalyticsReport,
  getClientAnalyticsLink,
} from "../../../services/integrationsService";
import { CLIENT_ERROR_MESSAGE, googleReauthMessage, isGoogleReauthError } from "./googleReauth";

// Headline cards, in the order the client's mockup shows them.
// Icons follow the app's MetricCard convention (Dashboard, Team): an icon
// before the figure, alternating neutral and primary tints.
const KPI_CARDS = [
  { key: "totalUsers", label: "Usuarios", icon: Users, tone: "neutral" },
  { key: "sessions", label: "Sesiones", icon: MousePointerClick, tone: "primary" },
  { key: "keyEvents", label: "Eventos clave", icon: Target, tone: "neutral" },
  {
    key: "sessionKeyEventRate",
    label: "Tasa de conversión",
    isRate: true,
    icon: Percent,
    tone: "primary",
  },
];

// Same client-side re-read as the channel tabs, applied to the pages list.
const PAGE_VIEWS = [
  { value: "sessions", label: "Sesiones" },
  { value: "key_events", label: "Eventos clave" },
  { value: "conversion", label: "Conversión" },
];

const PAGE_VIEW_METRIC = {
  sessions: (page) => page.sessions ?? 0,
  key_events: (page) => page.key_events ?? 0,
  conversion: (page) => page.conversion_rate ?? 0,
};

const KPI_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
};

// The three readings of the channel table. GA4 already returns all three
// metrics on every channel row, so the tabs are a client-side re-read of the
// same report: they pick what the bar draws, how the rows sort, and which
// column is highlighted. No extra request is made when the tab changes.
const CHANNEL_VIEWS = [
  { value: "sessions", label: "Sesiones" },
  { value: "key_events", label: "Eventos clave" },
  { value: "conversion", label: "Conversión" },
];

const CHANNEL_VIEW_METRIC = {
  sessions: (channel) => channel.sessions ?? 0,
  key_events: (channel) => channel.key_events ?? 0,
  conversion: (channel) => channel.conversion_rate ?? 0,
};

// GA4 returns channel groups in English; the dashboard is Spanish.
const CHANNEL_LABELS = {
  "Organic Search": "Búsqueda orgánica",
  Direct: "Tráfico directo",
  "Paid Search": "Google Ads",
  "Organic Social": "Redes sociales",
  "Paid Social": "Redes sociales de pago",
  Referral: "Referencias",
  Email: "Email",
  Display: "Display",
  "Organic Video": "Vídeo orgánico",
  Unassigned: "Sin asignar",
};

// Each channel's bar gets its own color, following the mockup: paid traffic in
// the warning tone, organic/owned traffic in shades of primary, the rest grey.
const channelColor = (channel, theme) => {
  switch (channel) {
    case "Organic Search":
      return theme.palette.primary.main;
    case "Direct":
      return theme.palette.primary.light;
    case "Paid Search":
    case "Paid Social":
      return theme.palette.warning.main;
    case "Organic Social":
      return theme.palette.warning.light;
    default:
      return theme.palette.grey[300];
  }
};

const PAGES_PAGE_SIZE = 10;

// Grid tracks for the two AnalyticsTables. Below the min width each table
// scrolls sideways inside its own card instead of stretching the page.
const CHANNEL_TABLE_MIN_WIDTH = 520;
const PAGE_TABLE_MIN_WIDTH = 440;
const METRIC_TRACK = "minmax(84px, 1fr)";

const CHANNEL_COLUMNS_SKELETON = [
  { key: "channel", header: "Canal", width: "minmax(160px, 1.7fr)" },
  { key: "sessions", header: "Sesiones", align: "right", width: METRIC_TRACK },
  { key: "share", header: "Cuota", align: "right", width: METRIC_TRACK },
  { key: "key_events", header: "Ev. clave", align: "right", width: METRIC_TRACK },
  { key: "conversion", header: "Conv.", align: "right", width: METRIC_TRACK },
];

const PAGE_COLUMNS_SKELETON = [
  { key: "path", header: "Página de entrada", width: "minmax(160px, 2fr)" },
  { key: "sessions", header: "Sesiones", align: "right", width: METRIC_TRACK },
  { key: "key_events", header: "Ev. clave", align: "right", width: METRIC_TRACK },
  { key: "conversion", header: "Conv.", align: "right", width: METRIC_TRACK },
];

// A rate over zero sessions is undefined; the backend sends null and we show a dash.
const formatRateOrDash = (value) =>
  value === null || value === undefined ? "—" : formatPercent(value);

// GA4 reports sessions that never fired a page_view under this landing page.
const NOT_SET_LANDING_LABEL = "(sin página de entrada)";

const PAGES_TITLE = "Páginas de entrada que generan resultados";
const PAGES_SUBTITLE = "Sesiones y eventos clave de GA4 por página de entrada";

const userFacingError = (err) => {
  if (isGoogleReauthError(err)) {
    return googleReauthMessage(err, "pulsa «Configurar conexión» y vuelve a autorizarla");
  }
  const status = err?.response?.status;
  if (status === 502) {
    return (
      err?.response?.data?.detail ||
      "Google Analytics no ha respondido. Vuelve a intentarlo en unos minutos."
    );
  }
  if (status === 403 || status === 404) {
    return "No tienes acceso a las métricas de este cliente.";
  }
  return "No se han podido cargar los datos de Google Analytics.";
};

/** Percent change, or points for rates; a dash when there is no previous period. */
function Delta({ percent, points }) {
  const isPoints = percent === null || percent === undefined;
  return (
    <TrendDelta
      value={percent ?? points}
      suffix={isPoints ? " pts" : "%"}
      showEmpty
    />
  );
}

/** Small change line under a channel-table figure, right-aligned with it. */
function ChannelDelta({ value, suffix }) {
  return (
    <TrendDelta
      value={value}
      suffix={suffix}
      size="small"
      showEmpty
      sx={{ marginLeft: "auto", display: "flex", justifyContent: "flex-end" }}
    />
  );
}

/** Headline figure on the app's standard MetricCard, with its delta beside it. */
function KpiCard({ card, kpi, isLoading, showDelta, showPrevious }) {
  const { colors, neutrals } = useCustomTheme();
  const isPrimary = card.tone === "primary";
  const iconProps = {
    icon: card.icon,
    iconColor: isPrimary ? colors.primary.main : neutrals.gray[600],
    iconBackgroundColor: isPrimary ? `${colors.primary.main}15` : neutrals.gray[100],
  };

  if (isLoading) {
    return <MetricCard {...iconProps} label={card.label} value="" isLoading />;
  }
  if (!kpi) return null;

  return (
    <MetricCard
      {...iconProps}
      label={card.label}
      value={card.isRate ? formatPercent(kpi.value * 100) : formatNumber(kpi.value)}
      valueAdornment={
        showDelta ? (
          <Delta percent={kpi.change_percent} points={kpi.change_points} />
        ) : null
      }
      footer={
        showPrevious ? (
          <Typography variant="caption" color="text.disabled">
            {`Antes: ${
              card.isRate ? formatPercent(kpi.previous * 100) : formatNumber(kpi.previous)
            }`}
          </Typography>
        ) : null
      }
      sx={{ height: "100%", minWidth: 0 }}
    />
  );
}

/**
 * Two cards, side by side above the `lg` breakpoint; stacked in series below
 * it, so a narrow viewport never has to squeeze both into one row.
 */
function TwoColumnGrid({ left, right, columnsLg = "minmax(0, 1.7fr) minmax(0, 1fr)" }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: columnsLg,
        },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      {left}
      {right}
    </Box>
  );
}

/** Chart card + "Objetivos del negocio" and "Dispositivos" cards, while the report is loading. */
function ChartAndGoalsSkeleton({ keyEventRows }) {
  return (
    <TwoColumnGrid
      left={
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ padding: { xs: 2, sm: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
            <Skeleton variant="text" width={260} />
            <Skeleton variant="rounded" width={300} height={30} sx={{ marginTop: 2 }} />
            <Skeleton
              variant="rounded"
              sx={{ flex: 1, minHeight: TRAFFIC_CHART_PLOT_HEIGHT, height: "auto", marginTop: 2 }}
            />
          </CardContent>
        </Card>
      }
      right={
        <Stack spacing={2} sx={{ height: "100%" }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
              <Skeleton variant="text" width={200} />
              <Skeleton variant="text" width={220} />

              <Stack spacing={2} sx={{ marginTop: 2 }}>
                {Array.from({ length: keyEventRows }).map((_, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" width={50} />
                    </Stack>
                    <Skeleton
                      variant="rounded"
                      height={6}
                      sx={{ borderRadius: 3, marginTop: 0.5 }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={200} />
              <Skeleton variant="rounded" height={10} sx={{ borderRadius: 5, marginTop: 2 }} />
              <Skeleton variant="text" width="60%" sx={{ marginTop: 1 }} />
            </CardContent>
          </Card>
        </Stack>
      }
    />
  );
}

/**
 * "Origen del tráfico" + "Páginas de entrada que generan resultados", while the report
 * is loading. Row counts follow the previous report when there is one, so the
 * skeleton doesn't jump when the user only changes the date range.
 */
function ChannelsAndPagesSkeleton({
  channelRows,
  pageRows,
  channelView,
  pagesView,
  onChannelViewChange,
  onPagesViewChange,
}) {
  return (
    <TwoColumnGrid
      columnsLg="minmax(0, 1fr) minmax(0, 1fr)"
      left={
        <AnalyticsTable
          title="Origen del tráfico"
          subtitle="Qué canales traen visitas y cuáles traen resultados · Google Analytics"
          tabs={CHANNEL_VIEWS}
          activeTab={channelView}
          onTabChange={onChannelViewChange}
          tabsDisabled
          columns={CHANNEL_COLUMNS_SKELETON}
          rows={[]}
          isLoading
          skeletonRows={channelRows}
          minTableWidth={CHANNEL_TABLE_MIN_WIDTH}
        />
      }
      right={
        <AnalyticsTable
          title={PAGES_TITLE}
          subtitle={PAGES_SUBTITLE}
          tabs={PAGE_VIEWS}
          activeTab={pagesView}
          onTabChange={onPagesViewChange}
          tabsDisabled
          columns={PAGE_COLUMNS_SKELETON}
          rows={[]}
          isLoading
          skeletonRows={pageRows}
          minTableWidth={PAGE_TABLE_MIN_WIDTH}
        />
      }
    />
  );
}

/**
 * `readOnly` is the client's own metrics page: no connect/configure actions
 * and no toolbar card. There the page owns the date bar (it sits in the page
 * header) and passes the selection down through `dates`. Elsewhere the panel
 * draws its own bar, controlled through `dates` + `onDatesChange` when given.
 */
export function TrafficPanel({
  clientId,
  readOnly = false,
  dates: datesProp,
  onDatesChange,
}) {
  const theme = useTheme();

  const [link, setLink] = useState(null);
  const [report, setReport] = useState(null);
  const [isLoadingLink, setIsLoadingLink] = useState(true);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [channelView, setChannelView] = useState("sessions");
  const [pagesPageIndex, setPagesPageIndex] = useState(0);
  const [pagesView, setPagesView] = useState("sessions");

  // GA4 always reports one window against another, so the panel owns both.
  const [localDates, setDates] = useState(defaultDateSelection);
  const dates = datesProp || localDates;
  const handleDatesChange = onDatesChange || setDates;

  const startDate = toIsoDate(dates.range?.from);
  const endDate = toIsoDate(dates.range?.to);

  // With the comparison switched off the report still needs a second window to
  // come back well formed; the UI simply stops drawing it.
  const compareStartDate = toIsoDate(dates.compareRange?.from);
  const compareEndDate = toIsoDate(dates.compareRange?.to);

  useEffect(() => {
    let cancelled = false;

    const loadLink = async () => {
      setIsLoadingLink(true);
      try {
        const found = await getClientAnalyticsLink(clientId);
        if (!cancelled) setLink(found);
      } catch (err) {
        if (!cancelled) setError(userFacingError(err));
      } finally {
        if (!cancelled) setIsLoadingLink(false);
      }
    };

    loadLink();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Refetch whenever the property or either window changes.
  useEffect(() => {
    if (!link?.id || !startDate || !endDate) return undefined;

    let cancelled = false;

    const loadReport = async () => {
      setIsLoadingReport(true);
      setError("");
      try {
        const data = await getAnalyticsReport(link.id, {
          startDate,
          endDate,
          compareStartDate,
          compareEndDate,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (cancelled) return;
        setError(userFacingError(err));
        setReport(null);
      } finally {
        if (!cancelled) setIsLoadingReport(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [link?.id, startDate, endDate, compareStartDate, compareEndDate]);

  const handleConnected = (newLink) => {
    setLink(newLink);
  };

  const handleDisconnected = () => {
    setLink(null);
    setReport(null);
    setError("");
  };

  // Rows follow the selected tab: the metric it names decides the ordering and
  // the length of each bar. "Sesiones" reuses the share GA4 already computed;
  // the other two scale each row against the largest value in the column.
  const sortedChannels = useMemo(() => {
    const metric = CHANNEL_VIEW_METRIC[channelView];
    return [...(report?.channels || [])].sort((a, b) => metric(b) - metric(a));
  }, [report, channelView]);

  const channelMetricMax = useMemo(() => {
    const metric = CHANNEL_VIEW_METRIC[channelView];
    return Math.max(0, ...sortedChannels.map(metric));
  }, [sortedChannels, channelView]);

  const channelBarValue = (channel) => {
    if (channelView === "sessions") return channel.share ?? 0;
    if (!channelMetricMax) return 0;
    return (CHANNEL_VIEW_METRIC[channelView](channel) / channelMetricMax) * 100;
  };

  // Keep the previous report's row counts while a new one loads, so the
  // skeleton doesn't jump; fall back to sensible defaults the first time.
  const channelSkeletonRows = report?.channels?.length || 5;
  const keyEventSkeletonRows = report?.key_events?.length || 4;
  const pageSkeletonRows = Math.min(report?.pages?.length || 5, PAGES_PAGE_SIZE);

  const pages = useMemo(() => {
    const metric = PAGE_VIEW_METRIC[pagesView];
    return [...(report?.pages || [])].sort((a, b) => metric(b) - metric(a));
  }, [report, pagesView]);

  // A new report or a new ordering invalidates whatever page of "Páginas que
  // generan resultados" was being browsed.
  useEffect(() => {
    setPagesPageIndex(0);
  }, [pages]);

  // Each figure cell: the value, plus the growth line when comparing periods.
  const figureWithDelta = (figure, delta) => (
    <Stack alignItems="flex-end" spacing={0.25}>
      <span>{figure}</span>
      {delta}
    </Stack>
  );

  const channelColumns = [
    {
      key: "channel",
      header: "Canal",
      width: CHANNEL_COLUMNS_SKELETON[0].width,
      cellSx: { color: "text.primary" },
      render: (channel) => (
        <Stack spacing={0.5} sx={{ width: "100%", minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {CHANNEL_LABELS[channel.channel] || channel.channel}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(channelBarValue(channel), 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.grey[300], 0.4),
              "& .MuiLinearProgress-bar": {
                backgroundColor: channelColor(channel.channel, theme),
                borderRadius: 3,
                transition: "transform 0.3s ease",
              },
            }}
          />
        </Stack>
      ),
    },
    {
      key: "sessions",
      header: "Sesiones",
      align: "right",
      width: METRIC_TRACK,
      highlighted: channelView === "sessions",
      render: (channel) =>
        figureWithDelta(
          formatNumber(channel.sessions),
          dates.compareEnabled && (
            <ChannelDelta value={channel.change_percent} suffix="%" />
          )
        ),
    },
    {
      key: "share",
      header: "Cuota",
      align: "right",
      width: METRIC_TRACK,
      render: (channel) => formatPercent(channel.share),
    },
    {
      key: "key_events",
      header: "Ev. clave",
      align: "right",
      width: METRIC_TRACK,
      highlighted: channelView === "key_events",
      render: (channel) =>
        figureWithDelta(
          formatNumber(channel.key_events),
          dates.compareEnabled && (
            <ChannelDelta value={channel.key_events_change_percent} suffix="%" />
          )
        ),
    },
    {
      key: "conversion",
      header: "Conv.",
      align: "right",
      width: METRIC_TRACK,
      highlighted: channelView === "conversion",
      render: (channel) =>
        figureWithDelta(
          formatRateOrDash(channel.conversion_rate),
          dates.compareEnabled && (
            <ChannelDelta
              value={channel.conversion_rate_change_points}
              suffix=" pts"
            />
          )
        ),
    },
  ];

  const pageColumns = [
    {
      key: "path",
      header: "Página",
      width: PAGE_COLUMNS_SKELETON[0].width,
      cellSx: { color: "text.primary" },
      render: (page) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            minWidth: 0,
            ...(page.is_not_set && { fontStyle: "italic", color: "text.secondary" }),
          }}
          noWrap
          title={page.is_not_set ? "Sesiones sin ninguna página vista" : page.path}
        >
          {page.is_not_set ? NOT_SET_LANDING_LABEL : page.path}
        </Typography>
      ),
    },
    {
      key: "sessions",
      header: "Sesiones",
      align: "right",
      width: METRIC_TRACK,
      highlighted: pagesView === "sessions",
      render: (page) =>
        figureWithDelta(
          formatNumber(page.sessions),
          dates.compareEnabled && (
            <ChannelDelta value={page.change_percent} suffix="%" />
          )
        ),
    },
    {
      key: "key_events",
      header: "Ev. clave",
      align: "right",
      width: METRIC_TRACK,
      highlighted: pagesView === "key_events",
      render: (page) =>
        figureWithDelta(
          formatNumber(page.key_events),
          dates.compareEnabled && (
            <ChannelDelta value={page.key_events_change_percent} suffix="%" />
          )
        ),
    },
    {
      key: "conversion",
      header: "Conv.",
      align: "right",
      width: METRIC_TRACK,
      highlighted: pagesView === "conversion",
      render: (page) => formatRateOrDash(page.conversion_rate),
    },
  ];

  // Goals include configured key events with no data, so "with data" is counted.
  const goalsWithData = (report?.key_events || []).filter((e) => e.count > 0).length;
  const configuredGoals = report?.configured_key_events_count;
  const goalsSummary =
    configuredGoals !== null && configuredGoals !== undefined
      ? `${goalsWithData} de ${configuredGoals} ${
          configuredGoals === 1 ? "objetivo configurado" : "objetivos configurados"
        } con datos en este periodo.`
      : `${goalsWithData} ${goalsWithData === 1 ? "objetivo" : "objetivos"} con datos en este periodo.`;

  const channelsWithoutSessions = report?.channels_without_sessions || [];

  const pagesRangeStart = Math.min(pagesPageIndex * PAGES_PAGE_SIZE + 1, pages.length);
  const pagesRangeEnd = Math.min((pagesPageIndex + 1) * PAGES_PAGE_SIZE, pages.length);
  const pagesForCurrentPage = pages.slice(
    pagesPageIndex * PAGES_PAGE_SIZE,
    (pagesPageIndex + 1) * PAGES_PAGE_SIZE
  );

  const pagesWithSessions = report?.pages_total?.pages_with_sessions ?? pages.length;
  const pagesFooterText =
    pagesWithSessions > pages.length
      ? `Mostrando ${pagesRangeStart}-${pagesRangeEnd} de las ${pages.length} páginas de entrada con más sesiones (${pagesWithSessions} en total).`
      : `Mostrando ${pagesRangeStart}-${pagesRangeEnd} de ${pages.length} páginas de entrada.`;

  if (isLoadingLink) {
    return (
      <Card>
        <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={220} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!link) {
    return (
      <>
        <Card>
          <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <EmptyStateMessage
              icon={Users}
              message={
                error ||
                "Este cliente todavía no tiene una propiedad de Google Analytics vinculada."
              }
              sx={{ py: 5 }}
            />
            {!readOnly && (
              <Stack alignItems="center">
                <Button variant="outline" onClick={() => setIsModalOpen(true)}>
                  <GoogleAnalyticsIcon size={16} style={{ marginRight: 8 }} />
                  Conectar Google Analytics
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>

        {!readOnly && (
          <ConnectGoogleAnalyticsModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            clientId={clientId}
            currentLink={null}
            onConnected={handleConnected}
          />
        )}
      </>
    );
  }

  return (
    <Stack spacing={2}>
      {readOnly && error && <Alert severity="error">{CLIENT_ERROR_MESSAGE}</Alert>}

      {!readOnly && (
      <Card>
        <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "flex-end" }}
            flexWrap="wrap"
            useFlexGap
            spacing={2}
          >
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
              <MetricsDateRangeBar
                value={dates}
                onChange={handleDatesChange}
                disabled={isLoadingReport}
              />
            </Box>

            {!readOnly && (
              <Button variant="outline" onClick={() => setIsModalOpen(true)}>
                <GoogleAnalyticsIcon size={16} style={{ marginRight: 8 }} />
                Configurar conexión
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {error}
            </Alert>
          )}

          {report?.domain_mismatch && (
            <AlertNote variant="warning" sx={{ marginTop: 2 }}>
              {`La propiedad de GA4 vinculada (${report.domain_mismatch.property_host}) no coincide con la web del cliente (${report.domain_mismatch.client_host}).`}
            </AlertNote>
          )}
        </CardContent>
      </Card>
      )}

      {(isLoadingReport || report) && (
        <Box sx={KPI_GRID_SX}>
          {KPI_CARDS.map((card) => (
            <KpiCard
              key={card.key}
              card={card}
              kpi={report?.kpis?.[card.key]}
              isLoading={isLoadingReport}
              showDelta={dates.compareEnabled}
              showPrevious={dates.compareEnabled && dates.showPreviousValues}
            />
          ))}
        </Box>
      )}

      {isLoadingReport && <ChartAndGoalsSkeleton keyEventRows={keyEventSkeletonRows} />}

      {report && !isLoadingReport && (
        <TwoColumnGrid
          left={
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ padding: { xs: 2, sm: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Evolución del tráfico y los objetivos
                </Typography>

                {report.timeseries?.current?.length > 0 ? (
                  <Box sx={{ marginTop: 2, flex: 1 }}>
                    <TrafficChart
                      timeseries={report.timeseries}
                      compareEnabled={dates.compareEnabled}
                    />
                  </Box>
                ) : (
                  <EmptyStateMessage
                    icon={Users}
                    message="GA4 no ha devuelto una serie temporal para este periodo."
                    sx={{ py: 4 }}
                  />
                )}
              </CardContent>
            </Card>
          }
          right={
            <Stack spacing={2} sx={{ height: "100%" }}>
              <Card sx={{ flex: 1 }}>
                <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Objetivos del negocio
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Eventos clave de GA4 · medición activa
                  </Typography>

                  <Stack spacing={2} sx={{ marginTop: 2 }}>
                    {report.key_events.map((event) => (
                      <Box key={event.event_name}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {event.label}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              {formatNumber(event.count)}
                            </Typography>
                            {dates.compareEnabled && (
                              <Delta percent={event.change_percent} points={null} />
                            )}
                          </Stack>
                        </Stack>
                        <Progress
                          soft
                          height={6}
                          value={Math.min(
                            (event.count /
                              Math.max(...report.key_events.map((e) => e.count), 1)) *
                              100,
                            100
                          )}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ))}
                  </Stack>

                  {report.key_events.length === 0 && (
                    <EmptyStateMessage
                      icon={Users}
                      message="Este cliente no tiene eventos clave marcados en GA4 ni han registrado datos en ninguno de los dos periodos."
                      sx={{ py: 3 }}
                    />
                  )}

                  {report.key_events.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", marginTop: 2 }}
                    >
                      {goalsSummary}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {report.devices.length > 0 && (
                <Card>
                  <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Dispositivos
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reparto de las sesiones del periodo
                    </Typography>
                    <Box sx={{ marginTop: 2 }}>
                      <DeviceShare devices={report.devices} />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Stack>
          }
        />
      )}

      {isLoadingReport && (
        <ChannelsAndPagesSkeleton
          channelRows={channelSkeletonRows}
          pageRows={pageSkeletonRows}
          channelView={channelView}
          pagesView={pagesView}
          onChannelViewChange={setChannelView}
          onPagesViewChange={setPagesView}
        />
      )}

      {report && !isLoadingReport && (
        <TwoColumnGrid
          columnsLg="minmax(0, 1fr) minmax(0, 1fr)"
          left={
            <AnalyticsTable
              title="Origen del tráfico"
              subtitle="Qué canales traen visitas y cuáles traen resultados · Google Analytics"
              tabs={CHANNEL_VIEWS}
              activeTab={channelView}
              onTabChange={setChannelView}
              columns={channelColumns}
              rows={sortedChannels}
              getRowKey={(channel) => channel.channel}
              minTableWidth={CHANNEL_TABLE_MIN_WIDTH}
              emptyState={
                <EmptyStateMessage
                  icon={Users}
                  message="GA4 no ha devuelto tráfico en este periodo."
                  sx={{ py: 3 }}
                />
              }
              footer={
                channelsWithoutSessions.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {`Sin sesiones en este periodo (no se muestran): ${channelsWithoutSessions
                      .map((name) => CHANNEL_LABELS[name] || name)
                      .join(", ")}.`}
                  </Typography>
                ) : null
              }
            />
          }
          right={
            <AnalyticsTable
              title={PAGES_TITLE}
              subtitle={PAGES_SUBTITLE}
              tabs={PAGE_VIEWS}
              activeTab={pagesView}
              onTabChange={setPagesView}
              columns={pageColumns}
              rows={pagesForCurrentPage}
              getRowKey={(page) => page.path}
              minTableWidth={PAGE_TABLE_MIN_WIDTH}
              emptyState={
                <EmptyStateMessage
                  icon={BarChart3}
                  message="Todavía no hay páginas con datos en este periodo."
                  sx={{ py: 4 }}
                />
              }
              footer={
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="caption" color="text.secondary">
                    {pagesFooterText}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <IconButton
                      size="small"
                      aria-label="Página anterior"
                      disabled={pagesPageIndex === 0}
                      onClick={() => setPagesPageIndex((page) => page - 1)}
                    >
                      <ChevronLeft size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Página siguiente"
                      disabled={pagesRangeEnd >= pages.length}
                      onClick={() => setPagesPageIndex((page) => page + 1)}
                    >
                      <ChevronRight size={16} />
                    </IconButton>
                  </Stack>
                </Stack>
              }
            />
          }
        />
      )}

      {!readOnly && (
        <ConnectGoogleAnalyticsModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          clientId={clientId}
          currentLink={link}
          onConnected={handleConnected}
          onDisconnected={handleDisconnected}
        />
      )}
    </Stack>
  );
}

export default TrafficPanel;
